import { ref, useNuxtApp, useRequestHeaders } from '#imports'
import { onClickOutside, unrefElement, useEventListener } from '@vueuse/core'
import UAParser from 'ua-parser-js'
import { DOMAIN_LIST } from '~/types/constants'
import { OSName } from '~/types/enum'
import { getAppMetaData } from './jsBridge'

const isEpubFile = (extension: PPFile['file_extension'], mimeType: PPFile['mime_type']) =>
  (/epub$/i.test(extension) && /^application\/(epub\+)?zip$/.test(mimeType)) || mimeType === 'application/epub+zip'

/* 根据后缀判断是否为支持的解压文件 */
export const isZippedFile = (extension: PPFile['file_extension'], mimeType: PPFile['mime_type']) => {
  if (isEpubFile(extension, mimeType)) return false
  return /(7z|zip|rar)$/i.test(extension) || /(x-7z-compressed)|(x-rar-compressed)|(zip)$/.test(mimeType)
}

/* 根据后缀判断是否为图片 */
export const isImage = (extension: PPFile['file_extension'], mimeType: PPFile['mime_type']) =>
  /(png|jpeg|bmp|jpg|icon|jpe|gif|jpeg2000)$/i.test(extension) || mimeType.startsWith('image/')

/* 根据后缀判断是否为视频 */
export const isVideo = (extension: PPFile['file_extension'], mimeType: PPFile['mime_type']) =>
  /(wmv|asf|asx|rm|rmvb|mpg|mpeg|mpe|3gp|mov|mp4|webm|m4v|avi|mkv|flv|f4v|vob|ts|xv)$/i.test(extension) ||
  (mimeType.startsWith('video/') && mimeType !== 'video/webp_pipe')

/* 根据后缀判断是否为音频 */
export const isAudio = (extension: PPFile['file_extension'], mimeType: PPFile['mime_type']) =>
  /(wav|midi|cda|mp3|mp3pro|wma|sacd|vqf|ra|voc|au|aif|snd|aac|flac|ape)$/i.test(extension) ||
  mimeType.startsWith('audio/')

/**
 * 将字节（Byte)单位转换为 KB，MB，或者 GB，甚至 TB
 * 整数位不超过1024，超过则升级单位，小数位两位(KB 除外)
 * @param size 单位字节 Bytes 的大小
 * @param fractionDigits 小数的个数
 */
export const storageUnit = (size: number | string, fractionDigits = 1): string => {
  const val = Number(size)
  if (Number.isNaN(val)) return '*'
  if (Number.isNaN(Number(fractionDigits))) {
    throw new TypeError('The second argument of storageUnit() must be a number or a number string')
  }
  const unitSize = 1024
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let deep = 0
  let result = val
  while (deep < units.length && result >= unitSize) {
    result /= unitSize
    deep += 1
  }
  const storage = deep
    ? result.toString().replace(/\.\d*$/, $1 => $1.slice(0, Number(fractionDigits) ? Number(fractionDigits) + 1 : 0))
    : result

  return `${storage} ${units[deep]}`
}

/* 监听指定元素之外的右键事件 */

// @ts-ignore
export const onRightClickOutside: typeof onClickOutside = (target, handler) => {
  if (!window) return undefined

  const shouldListen = ref(true)

  const listener = (event: PointerEvent) => {
    const el = unrefElement(target)

    if (!el || el === event.target || event.composedPath().includes(el) || !shouldListen.value) return

    handler(event)
  }

  const cleanup = [
    useEventListener(window, 'contextmenu', listener, { passive: true, capture: true }),
    useEventListener(
      window,
      'pointerdown',
      e => {
        const el = unrefElement(target)
        shouldListen.value = !!el && !e.composedPath().includes(el)
      },
      { passive: true },
    ),
  ]

  return () => cleanup.forEach(fn => fn())
}

/**
 * 保存key-value键值对信息到localStorage
 * @param key 需要存储的key名称
 * @param value 需要存储的value名称
 * @returns { result, error }
 */
export const saveKVToLocalStorage = (key: string, value: { [key: string]: unknown } | string) => {
  try {
    const prefix = 'pp_web_'
    let data = value as string
    if (typeof value !== 'string') {
      data = JSON.stringify(value)
    }
    window.localStorage.setItem(prefix + key, data)
    return { result: 0, error: null }
  } catch (err) {
    console.log('error: ', err)
    return { result: null, error: err }
  }
}

/**
 * 从localStorage读取key-value键值对信息
 * @param key 需要取值的key名称
 * @returns { result, error }
 */
export const getKVFromLocalStorage = (key: string) => {
  try {
    const prefix = 'pp_web_'
    const data = window.localStorage.getItem(prefix + key)
    if (data === null) {
      throw new Error('empty response.')
    }
    try {
      return { result: JSON.parse(data), error: null }
    } catch {
      return { result: data, error: null }
    }
  } catch (err) {
    return { result: null, error: err }
  }
}

/**
 * 记录视频播放记录的队列, 只存储最近30条播放记录
 */
export const videoPlayedQueue = (() => {
  const key = 'video_play_record'
  const SIZE = 30
  return {
    /* eslint-disable-next-line */
    push(videoId: string, value: any) {
      let { result: data } = getKVFromLocalStorage(key)
      if (Array.isArray(data)) {
        /* eslint-disable-next-line */
        const existItem = data.find(item => (item as any).videoId === videoId)
        if (existItem) {
          existItem.value = value
        } else {
          data.push({ videoId, value })
        }
        if (data.length > SIZE) {
          data = data.slice(data.length - SIZE)
        }
      } else {
        data = [{ videoId, value }]
      }
      saveKVToLocalStorage(key, JSON.stringify(data))
    },

    get(videoId: string) {
      const { result: data } = getKVFromLocalStorage(key)
      if (data && Array.isArray(data)) {
        return data.find(item => item.videoId === videoId)
      }
      return null
    },
  }
})()

/**
 * 格式化时间字符串, 单位ms
 *  */
export const formatSecondString = (second: number) => {
  let seconds = Math.ceil(second)
  let hour = '00'
  let minute = '00'
  if (seconds >= 3600) {
    const num = Math.floor(seconds / 3600)
    hour = String(num).padStart(2, '0')
    seconds -= num * 3600
  }
  if (seconds >= 60) {
    const num = Math.floor(seconds / 60)
    minute = String(num).padStart(2, '0')
    seconds -= num * 60
  }
  return `${hour}:${minute}:${String(seconds).padStart(2, '0')}`
}

/**
 * 登录成功后，会在 localStorage 写 credentials_{uid}，里面包含监权相关信息
 * 退出登录时，会删除这个 item
 * 所有可以用这个 item 判断是否登录
 */
export const authByLocalStorage = () => !!Object.keys(localStorage).find(el => el.includes('credentials_'))

export const isMacOS = () => new UAParser().getOS().name === OSName.MAC_OS
export const isWindows = () => new UAParser().getOS().name === OSName.WINDOWS

export const isIOS = () => new UAParser().getOS().name === OSName.IOS

export const isAndroid = () => new UAParser().getOS().name === OSName.ANDROID
export const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

export const getGlobal = () => {
  if (typeof self !== 'undefined') {
    return self
  }
  if (typeof window !== 'undefined') {
    return window
  }
  if (typeof global !== 'undefined') {
    return global
  }
  throw new Error('unable to locate global object')
}

/**
 * 优先使用 useDomain，特别是用于服务端渲染，useDomain 无法使用的场景「非 Nuxt 生命周期内」才使用 getDomain()
 */
export const getDomain = () => getGlobal().location?.hostname.replace(/[-\w]+\.(\w+\.\w+)/, '$1') || DOMAIN_LIST[0]

// 休眠函数
export const sleep = (time: number) =>
  new Promise(resolve => {
    setTimeout(resolve, time)
  })

// 生成授权码供 插件/pc 等端使用
export const generateAuthorizeCode = async ({ clientId, redirectUri }: { clientId: string; redirectUri: string }) => {
  try {
    const { code } = await window.loginUI.login.auth.userAuthorize({
      // 插件端的client_id
      client_id: clientId,
      state: 'ignored',
      scope: 'user+pan+offline',
      response_type: 'code',
      redirect_uri: redirectUri,
    })
    return code
  } catch (err) {
    console.error('generateAuthorizeCodeToPlugin error: ', err)
  }
}

// 获取cookie
export const getCookie = (cname: string) => {
  const name = `${cname}=`
  const ca = document.cookie.split(';')

  for (let i = 0; i < ca.length; i++) {
    const c = ca[i].trim()
    if (c.indexOf(name) === 0) return c.substring(name.length, c.length)
  }
  return ''
}
/* 编码 base64 */
export const btoaUTF16 = (sString: string): string => {
  const aUTF16CodeUnits = new Uint16Array(sString.length)
  const charCodeString = aUTF16CodeUnits.map((_, idx) => sString.charCodeAt(idx))
  return window.btoa(String.fromCharCode.apply(null, [...new Uint8Array(charCodeString.buffer)]))
}
/* 解码 base64 */
export const atobUTF16 = (sBase64: string): string => {
  const sBinaryString = atob(sBase64)
  const aBinaryView = new Uint8Array(sBinaryString.length)
  const charCodeString = aBinaryView.map((_, idx) => sBinaryString.charCodeAt(idx))
  return String.fromCharCode.apply(null, [...new Uint16Array(charCodeString.buffer)])
}

// 通过jsBridge同步客户端语言
export const synchronizeClientLanguage = () => {
  try {
    getAppMetaData({}, async info => {
      const clientInfo = typeof info === 'string' ? JSON.parse((info as string) || '{}') : info

      // @ts-ignore
      await useNuxtApp().$i18n.setLocale(clientInfo.language)
    })
  } catch (err) {
    console.error('get client language error: ', err)
  }
}

/**
 * 设我们当前所在的域名为 zhuanlan.zhihu.com， 想要获取二级域名， 得到 zhihu.com，<br/>
 * 如果我们所在的域名已经是 zhihu.com， 获取二级域名， 还是得到 zhihu.com，<br/>
 * 如果当前是 IP 地址，则返回后三个数字组成的IP地址（特殊逻辑，不具有通用性）
 */
export const getLevel2domain = () => {
  const domain = document.location.host
  try {
    let subdomain = ''
    const domainList = domain.split('.')

    const ipAddressReg =
      /^(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/

    /* 若当前域名为 IP 地址、localhost，采用一种特殊的处理
     （只用于我们的测试环境，此部分代码不具有通用性）
     */
    if (ipAddressReg.test(domain) || domain === 'localhost') {
      const pattern = /^.*?\b\.\b/
      return domain.replace(pattern, '')
    }

    const urlItems = []
    urlItems.unshift(domainList.pop())

    while (urlItems.length < 2) {
      urlItems.unshift(domainList.pop())
      subdomain = urlItems.join('.')
    }

    return subdomain || domain
  } catch {
    return domain
  }
}
/**
 * 判断是否为PikPak客户端
 */
export const isPikPakClient = (ua?: string) =>
  /PikPak/i.test(
    ua || (import.meta.client ? window.navigator.userAgent : useRequestHeaders(['user-agent'])['user-agent']) || '',
  )

export const formatNumber = (num: string) => {
  const [integer, decimal] = num.split('.')
  return `${integer.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')}.${decimal}`
}

/**
 * 根据后缀与 mimeType 判断是否为种子文件
 * @param extension { string } 文件后缀
 * @param mimeType { string }
 * @returns boolean
 */
export const isTorrentFile = (extension: PPFile['file_extension'], mimeType: PPFile['mime_type']) =>
  extension?.endsWith('.torrent') || mimeType?.endsWith('application/x-bittorrent')

// 获取格式为2023-10-23日期的时间戳
export const toTimestamp = (date: string) => {
  const newDate = date.match(/(\d{4}-\d{2}-\d{2})/)?.[0]
  if (!newDate) return 0
  const time = new Date(newDate)
  time.setHours(0, 0, 0, 0)
  return time.getTime()
}

export function formatDate(date: string): string {
  const dateObj = new Date(date)
  const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' }
  const formatter = new Intl.DateTimeFormat('en-US', options)
  return formatter.format(dateObj)
}

export const toBlog = () => `https://blog.${window.location.hostname.split('.').slice(-2).join('.')}`

const SHOW_BLOCK_BUTTON_KEY = 'PP_SHOW_BLOCK_BUTTON'
export const isShowBlockButton = () => localStorage.getItem(SHOW_BLOCK_BUTTON_KEY) === 'true'

/**
 * 格式化时间，提前天、时、分、秒
 * @param time 时间，以秒为单位
 * @returns
 */
export function durationFormatter(time: number) {
  if (!time) return { ss: 0 }
  let t = time
  const ss = Math.floor(t % 60)
  t = (t - ss) / 60
  if (t < 1) return { ss }
  const mm = Math.floor(t % 60)
  t = (t - mm) / 60
  if (t < 1) return { mm, ss }
  const hh = Math.floor(t % 24)
  t = (t - hh) / 24
  if (t < 1) return { hh, mm, ss }
  const dd = Math.floor(t)
  return {
    dd,
    hh,
    mm,
    ss,
  }
}

export const windowsOrigin = 'https://pc.mypikpak.com'

export const windowsClientId = process.dev ? 'Yvto-15vUZe3ya2q' : 'YvtoWO6GNHiuCl7x'

// video 标签是否支持该音频格式
export const isVideoSupportAudio = (code: string) => !['eac3'].includes(code)

/** 计算两个时间的天数 */
export function computeDurationDays(startTime: number, endTime: number) {
  const durations = endTime - startTime
  const days = Math.ceil(durations / (24 * 60 * 60 * 1000))
  return days || 0
}
